import {
  Modal,
  IconButton,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { FC, useMemo } from "react";
import { XClose } from "untitledui-js-base";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { Table } from "../Table";
import {
  CellContext,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { ApplicationFeeStatus } from "shared/types";
import { Applicant } from "types/application";
import { formatToCurrency } from "shared/utils";
import { getFullName } from "shared/utils/get-full-name";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { Money } from "types/landlord/reports";
import { Loader } from "shared/components/Loader";
import { logger } from "logger";

interface Props {
  onClose: () => void;
  applicationId: string;
  onSuccess?: () => void;
  applicantId?: string;
  queryKey?: unknown[];
}

const useApplicationMeta = (applicationId: string) => {
  return useQuery({
    queryKey: ["application", applicationId],
    queryFn: () =>
      instance(`/portal/v1/applications/${applicationId}`).then(
        ({ data }) => data
      ),
    enabled: Boolean(applicationId)
  });
};

const useRefundMutation = (
  applicationId: string,
  applicantId?: string,
  invalidateKeys?: unknown[],
  onClose?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      instance.post(
        applicantId
          ? `/portal/v1/applications/${applicationId}/applicants/${applicantId}/refund`
          : `/portal/v1/applications/${applicationId}/refund`
      ),
    onSuccess: () => {
      toast.success("Application successfully refunded");
      if (invalidateKeys?.length)
        queryClient
          .invalidateQueries({ queryKey: invalidateKeys })
          .catch((err) => {
            logger.error("Failed to invalidate queries", err);
          });
      onClose?.();
    },
    onError: (error) => {
      toast.error(`Error refunding applications: ${getErrorMessage(error)}`);
    }
  });
};

export const RefundModal: FC<Props> = ({
  onClose,
  applicationId,
  applicantId,
  queryKey
}) => {
  const applicationMeta = useApplicationMeta(applicationId);
  const { mutate: refund, isPending } = useRefundMutation(
    applicationId,
    applicantId,
    queryKey,
    onClose
  );

  const paidApplicants = useMemo(
    () =>
      (applicationMeta.data?.applicants || []).filter(
        (applicant: Applicant) =>
          applicant.application_fee_status === ApplicationFeeStatus.Paid
      ),
    [applicationMeta.data]
  );

  const applicant = useMemo(
    () =>
      paidApplicants.find(
        (applicant: Applicant) =>
          applicantId && String(applicant.id) === String(applicantId)
      ),
    [paidApplicants, applicantId]
  );

  const tableData = useMemo(() => {
    return applicant ? [applicant] : paidApplicants;
  }, [applicantId, applicant, paidApplicants]);

  const columns = useMemo(() => {
    return [
      {
        accessorFn: (data: Applicant) =>
          getFullName({
            firstName: data.first_name,
            lastName: data.last_name,
            middleName: data.middle_name,
            short: true
          }),
        header: "Applicant",
        cell: (data: CellContext<Applicant, string>) => (
          <p className="text-sm truncate text-content-1000 font-medium">
            {data.getValue()}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.created_at",
        header: "Charge date",
        size: 120,
        cell: (data: CellContext<Applicant, string>) => (
          <p className="text-sm text-content-400">
            {data.getValue() && moment(data.getValue()).isValid()
              ? moment(data.getValue()).format("ll")
              : "--"}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.total",
        header: "Amount",
        size: 120,
        cell: (data: CellContext<Applicant, Money>) => (
          <p className="text-sm text-content-400">
            {Boolean(data.getValue())
              ? formatToCurrency(data.getValue())
              : "--"}
          </p>
        )
      }
    ];
  }, []);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={onClose}
      panelClassName="max-w-[600px] w-full flex flex-col"
    >
      <div className="p-6 border-b border-alpha-100 flex items-center h-20 justify-between">
        <h1 className="text-xl text-content-1000 font-bold ">Confirm refund</h1>
        <IconButton
          onClick={onClose}
          borderLess
          className="ml-auto translate-x-2"
          Icon={XClose}
        />
      </div>
      <div className="bg-warning-50 grow-0 px-6 py-4 flex gap-4 items-center border-b border-alpha-100">
        <InfoIcon className="text-warning-500 h-5 w-5" />
        <p className="text-sm text-warning-700 font-medium">
          You cannot undo this action. Once a refund is issued it can take up to
          5-7 business days
        </p>
      </div>
      <div className="h-full grow-1 overflow-y-auto px-6 py-4 bg-background-900">
        {applicationMeta.isLoading ? (
          <div className="w-full flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-content-500 mb-2.5">
              These items will be refunded
            </p>
            <Table table={table} />
          </>
        )}
      </div>
      <div className="flex gap-2 h-[72px] justify-end px-6 py-4 border-t border-alpha-100">
        <Button
          className="w-[103px]"
          variant={ButtonVariant.BASE}
          onClick={() => onClose()}
          size={ButtonSize.SM}
        >
          Cancel
        </Button>
        <Button
          className="w-[103px]"
          variant={ButtonVariant.PRIMARY}
          onClick={() => refund()}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={applicationMeta.isLoading || isPending}
        >
          Refund
        </Button>
      </div>
    </Modal>
  );
};
